import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/context/AuthContext';
import { useChat } from '@/context/ChatContext';
import axios from 'axios';
import { handleWithFallback } from '@/lib/displayName';
import {
  Send,
  Loader2,
  MessageCircle,
  Smile,
  X,
  Users,
  ChevronDown,
  AtSign
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUICK_EMOJIS = ['👍', '🔥', '🛼', '🎶', '❤️', '😂', '🙌', '💜'];
// Reaction set is intentionally aligned with the DM ChatPage so users
// get a consistent vocabulary across both surfaces. Must match
// ALLOWED_EVENT_REACTIONS in /app/backend/routes/event_chat.py.
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// Notification sound - subtle ping
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch (e) {
    // Audio not supported
  }
};

// Browser notification
const showBrowserNotification = (senderName, content, eventName) => {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    new Notification(`${eventName} Chat`, {
      body: `${senderName}: ${content.substring(0, 80)}`,
      icon: '/icons/icon-192x192.png',
      tag: 'rollm8s-chat',
      silent: true
    });
  }
};

const requestNotificationPermission = () => {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
};

const GlobalEventChat = () => {
  const { user } = useAuth();
  const { activeChat, isOpen, unreadCount, closeChat, dismissChat, toggleChat, addUnread, clearUnread } = useChat();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showEmojis, setShowEmojis] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [participants, setParticipants] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [connected, setConnected] = useState(false);
  const [internalUnread, setInternalUnread] = useState(0);
  const [reactionPickerMsg, setReactionPickerMsg] = useState(null);
  const [replyTo, setReplyTo] = useState(null);   // Parent message being replied to (or null)
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const socketRef = useRef(null);
  const chatBodyRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const longPressTimerRef = useRef(null);
  const lastTypingRef = useRef(0);
  const lastMsgCountRef = useRef(0);
  const prevEventIdRef = useRef(null);

  const eventId = activeChat?.eventId;
  const eventName = activeChat?.eventName;
  const eventImage = activeChat?.eventImage;

  const scrollToBottom = useCallback((force = false) => {
    if (force || isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (!chatBodyRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatBodyRef.current;
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 60;
    if (isAtBottomRef.current) {
      setInternalUnread(0);
      clearUnread();
    }
  }, [clearUnread]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch chat history (merges with existing to avoid duplicates)
  const fetchHistory = useCallback(async (isInitial = false) => {
    if (!eventId) return;
    try {
      const res = await axios.get(`${API}/events/${eventId}/chat/history?limit=100`, {
        headers: getAuthHeaders()
      });
      const serverMsgs = res.data;

      setMessages(prev => {
        if (isInitial || prev.length === 0) {
          lastMsgCountRef.current = serverMsgs.length;
          if (isInitial) setTimeout(() => scrollToBottom(true), 100);
          return serverMsgs;
        }
        // Merge: remove local optimistic, add all server messages
        let merged = [...prev.filter(m => !m.message_id?.startsWith('local_'))];
        const mergedIds = new Set(merged.map(m => m.message_id));
        let newCount = 0;
        for (const m of serverMsgs) {
          if (!mergedIds.has(m.message_id)) {
            merged.push(m);
            mergedIds.add(m.message_id);
            newCount++;
          } else {
            // Update reactions etc
            const idx = merged.findIndex(x => x.message_id === m.message_id);
            if (idx !== -1) merged[idx] = m;
          }
        }
        merged.sort((a, b) => a.created_at.localeCompare(b.created_at));

        // Notify for new messages from others
        if (newCount > 0 && merged.length > lastMsgCountRef.current) {
          const latestNew = merged[merged.length - 1];
          if (latestNew.sender_id !== user?.user_id) {
            playNotificationSound();
            if (document.hidden) {
              showBrowserNotification(latestNew.sender_name, latestNew.content, eventName || 'Event');
            }
            if (!isAtBottomRef.current || !isOpen) {
              setInternalUnread(p => p + newCount);
              addUnread();
            } else {
              setTimeout(() => scrollToBottom(true), 50);
            }
          }
        }
        lastMsgCountRef.current = merged.length;
        return merged;
      });
      setLoading(false);
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setLoading(false);
    }
  }, [eventId, eventName, user, isOpen, scrollToBottom, addUnread]);

  // Fetch typing status via polling
  const fetchTyping = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await axios.get(`${API}/events/${eventId}/chat/typing`, {
        headers: getAuthHeaders()
      });
      setTypingUsers(res.data);
    } catch (e) {
      // ignore
    }
  }, [eventId]);

  // Fetch participants for @mentions
  const fetchParticipants = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await axios.get(`${API}/events/${eventId}/chat/participants`, {
        headers: getAuthHeaders()
      });
      setParticipants(res.data);
    } catch (e) {
      // ignore
    }
  }, [eventId]);

  // Reset when event changes
  useEffect(() => {
    if (eventId !== prevEventIdRef.current) {
      setMessages([]);
      setLoading(true);
      setTypingUsers([]);
      setInternalUnread(0);
      lastMsgCountRef.current = 0;
      prevEventIdRef.current = eventId;
    }
  }, [eventId]);

  // Main effect: Socket.IO + polling
  useEffect(() => {
    if (!eventId || !user) return;
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    if (!token) return;

    requestNotificationPermission();
    fetchHistory(true);
    fetchParticipants();

    const socket = io(BACKEND_URL, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      path: '/api/socket.io',
      forceNew: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      timeout: 15000
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setOnlineCount(prev => Math.max(prev, 1));
      socket.emit('join_event_chat', { token, event_id: eventId });
    });

    socket.on('connect_error', (err) => {
      console.warn('Event chat connect_error:', err.message);
    });

    socket.on('disconnect', () => {
      // Don't set connected=false — HTTP polling keeps the chat functional
    });

    // After 5s, ensure connected=true regardless (HTTP polling is the backbone)
    const fallbackTimer = setTimeout(() => {
      setConnected(true);
    }, 5000);

    // Poll for new messages every 3s as backup (works even if socket connected)
    const msgPoll = setInterval(() => {
      fetchHistory(false);
    }, 3000);

    socket.on('event_chat_new_message', (msg) => {
      setMessages(prev => {
        if (prev.some(m => m.message_id === msg.message_id)) return prev;
        if (msg.sender_id === user?.user_id) {
          const localIdx = prev.findIndex(m => m.message_id?.startsWith('local_') && m.content === msg.content);
          if (localIdx !== -1) {
            const updated = [...prev];
            updated[localIdx] = msg;
            return updated;
          }
        }
        // Sound + notification for messages from others
        if (msg.sender_id !== user?.user_id) {
          playNotificationSound();
          if (document.hidden) {
            showBrowserNotification(msg.sender_name, msg.content, eventName || 'Event');
          }
        }
        return [...prev, msg];
      });
      setParticipants(prev => {
        if (prev.some(p => p.user_id === msg.sender_id)) return prev;
        return [...prev, { user_id: msg.sender_id, name: msg.sender_name, picture: msg.sender_picture }];
      });
      if (!isAtBottomRef.current) {
        setInternalUnread(p => p + 1);
        addUnread();
      } else {
        setTimeout(() => scrollToBottom(true), 50);
      }
    });

    socket.on('chat_members_update', (data) => setOnlineCount(data.online_count || 0));

    socket.on('event_chat_reaction_update', (data) => {
      setMessages(prev => prev.map(m =>
        m.message_id === data.message_id ? { ...m, reactions: data.reactions } : m
      ));
    });

    // Fast polling: messages every 2s, typing every 1.5s
    const typePoll = setInterval(() => fetchTyping(), 1500);

    return () => {
      clearTimeout(fallbackTimer);
      clearInterval(msgPoll);
      clearInterval(typePoll);
      socket.emit('leave_event_chat', { event_id: eventId });
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [eventId, user, eventName, fetchHistory, fetchTyping, fetchParticipants, scrollToBottom, addUnread]);

  // Close reaction picker on scroll
  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    const close = () => setReactionPickerMsg(null);
    el.addEventListener('scroll', close);
    return () => el.removeEventListener('scroll', close);
  }, [isOpen]);

  const handleSend = async (e) => {
    e?.preventDefault();
    const content = newMessage.trim();
    if (!content) return;

    const mentionRegex = /@(\S+)/g;
    const mentions = [];
    let match;
    while ((match = mentionRegex.exec(content)) !== null) {
      mentions.push(match[1]);
    }

    // Snapshot the reply parent for the optimistic bubble so the user sees
    // the quoted preview straight away. Mirrors what the server stores.
    const replyParent = replyTo;
    const replySnapshot = replyParent ? {
      message_id: replyParent.message_id,
      sender_id: replyParent.sender_id,
      sender_name: replyParent.sender_name,
      sender_username: replyParent.sender_username,
      preview: (replyParent.content || '').slice(0, 137) + ((replyParent.content || '').length > 140 ? '...' : ''),
      message_type: replyParent.message_type || 'text',
    } : null;

    const optimisticMsg = {
      message_id: `local_${Date.now()}`,
      event_id: eventId,
      sender_id: user.user_id,
      sender_name: user.name,
      sender_username: user.username,
      sender_picture: user.selfie_url || user.picture,
      content,
      message_type: 'text',
      mentions,
      reply_to: replySnapshot,
      reactions: {},
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setTimeout(() => scrollToBottom(true), 50);
    setNewMessage('');
    setShowEmojis(false);
    setReplyTo(null);

    // Try socket first, fall back to HTTP POST
    const payload = { content, message_type: 'text', mentions, reply_to_message_id: replyParent?.message_id || null };
    if (socketRef.current?.connected) {
      socketRef.current.emit('event_chat_message', payload);
    } else {
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        await axios.post(`${API}/event-chat/${eventId}/messages`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } catch (err) {
        console.warn('HTTP send failed:', err);
      }
    }
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const startReply = (msg) => {
    setReplyTo(msg);
    setReactionPickerMsg(null);
    setTimeout(() => inputRef.current?.focus(), 60);
  };
  const cancelReply = () => setReplyTo(null);

  const handleEmojiSend = (emoji) => {
    if (!socketRef.current) return;
    const optimisticMsg = {
      message_id: `local_${Date.now()}`,
      event_id: eventId,
      sender_id: user.user_id,
      sender_name: user.name,
      sender_username: user.username,
      sender_picture: user.selfie_url || user.picture,
      content: emoji,
      message_type: 'emoji',
      mentions: [],
      reactions: {},
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setTimeout(() => scrollToBottom(true), 50);
    socketRef.current.emit('event_chat_message', { content: emoji, message_type: 'emoji', mentions: [] });
  };

  const handleReaction = (messageId, emoji) => {
    if (!socketRef.current) return;
    setMessages(prev => prev.map(m => {
      if (m.message_id !== messageId) return m;
      const reactions = { ...(m.reactions || {}) };
      const users = [...(reactions[emoji] || [])];
      const idx = users.indexOf(user.user_id);
      if (idx !== -1) users.splice(idx, 1);
      else users.push(user.user_id);
      if (users.length === 0) delete reactions[emoji];
      else reactions[emoji] = users;
      return { ...m, reactions };
    }));
    socketRef.current.emit('event_chat_react', { message_id: messageId, emoji });
    setReactionPickerMsg(null);
  };

  const handleTyping = (e) => {
    const val = e.target.value;
    setNewMessage(val);

    // Send typing status via REST (reliable through proxy)
    const now = Date.now();
    if (now - lastTypingRef.current > 1500) {
      lastTypingRef.current = now;
      axios.post(`${API}/events/${eventId}/chat/typing`, {}, { headers: getAuthHeaders() }).catch(() => {});
    }

    // @mentions detection
    const cursorPos = e.target.selectionStart;
    const textBefore = val.substring(0, cursorPos);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setShowMentions(true);
      setMentionFilter(atMatch[1].toLowerCase());
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (name) => {
    const cursorPos = inputRef.current?.selectionStart || newMessage.length;
    const textBefore = newMessage.substring(0, cursorPos);
    const textAfter = newMessage.substring(cursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    const newText = textBefore.substring(0, atIdx) + `@${name} ` + textAfter;
    setNewMessage(newText);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const handleLongPressStart = (msgId) => {
    longPressTimerRef.current = setTimeout(() => setReactionPickerMsg(msgId), 500);
  };

  const handleLongPressEnd = () => {
    clearTimeout(longPressTimerRef.current);
  };

  const formatTime = (dateStr) =>
    new Date(dateStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  const renderContent = (content) => {
    if (!content) return content;
    const parts = content.split(/(@\S+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return <span key={i} className="bg-primary/30 text-primary font-semibold rounded px-0.5">{part}</span>;
      }
      return part;
    });
  };

  const groupedMessages = messages.reduce((groups, msg) => {
    const d = new Date(msg.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    let label;
    if (d.toDateString() === today.toDateString()) label = 'Today';
    else if (d.toDateString() === yesterday.toDateString()) label = 'Yesterday';
    else label = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    if (!groups[label]) groups[label] = [];
    groups[label].push(msg);
    return groups;
  }, {});

  const typingNames = typingUsers.map(t => t.name).filter(n => n !== user?.name);

  const mentionList = participants.filter(p =>
    p.user_id !== user?.user_id && p.name?.toLowerCase().includes(mentionFilter)
  );

  if (!user || !activeChat) return null;

  // Floating button (when chat is closed)
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={toggleChat}
          className="bg-primary hover:bg-primary/90 text-white rounded-full p-4 shadow-lg shadow-primary/30 transition-all hover:scale-105 active:scale-95"
          data-testid="open-group-chat-btn"
        >
          <MessageCircle className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
              {unreadCount}
            </span>
          )}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); dismissChat(); }}
          className="absolute -top-2 -left-2 bg-muted hover:bg-destructive text-muted-foreground hover:text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md transition-colors"
          data-testid="dismiss-group-chat-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-50 w-full sm:w-[400px] h-[80vh] sm:h-[560px] bg-background/95 backdrop-blur-xl border border-border/50 sm:rounded-2xl rounded-t-2xl shadow-2xl shadow-primary/10 flex flex-col overflow-hidden" data-testid="event-group-chat">
      {/* Header — mirrors ChatPage style: avatar + title + meta */}
      <div className="bg-gradient-to-br from-primary/15 via-primary/8 to-transparent border-b border-border/50 px-4 py-3 flex items-center gap-3 shrink-0">
        <Avatar className="w-10 h-10 border-2 border-primary/40 shrink-0">
          {eventImage ? <AvatarImage src={eventImage} alt={eventName} /> : null}
          <AvatarFallback className="bg-primary/20 text-primary font-bold">
            {eventName?.charAt(0)?.toUpperCase() || 'E'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate flex items-center gap-1.5" data-testid="chat-event-name">
            {eventName || 'Event'}
            <span className="text-[10px] font-medium text-muted-foreground/80">· Group chat</span>
          </h3>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
            <span className="inline-flex items-center gap-1">
              {connected ? (
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.7)]" />
              ) : (
                <Loader2 className="w-3 h-3 animate-spin" />
              )}
              {connected ? 'Live' : 'Connecting…'}
            </span>
            <span className="text-muted-foreground/40">·</span>
            <span className="inline-flex items-center gap-1">
              <Users className="w-3 h-3" />
              {onlineCount} online
            </span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={closeChat} className="rounded-full h-8 w-8 p-0 hover:bg-foreground/10" data-testid="close-group-chat-btn">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Messages */}
      <div ref={chatBodyRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 py-4 space-y-2 relative">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-14">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <MessageCircle className="w-8 h-8 text-primary/70" />
            </div>
            <p className="text-sm font-semibold text-foreground">No messages yet</p>
            <p className="text-xs text-muted-foreground mt-1">Be the first to say hi 👋</p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              <div className="flex items-center justify-center my-4">
                <span className="px-3 py-1 rounded-full bg-card border border-border/40 text-xs text-muted-foreground">{date}</span>
              </div>
              {dateMessages.map((msg, idx) => {
                const isOwn = msg.sender_id === user?.user_id;
                const isEmoji = msg.message_type === 'emoji';
                const showAvatar = !isOwn && (idx === 0 || dateMessages[idx - 1]?.sender_id !== msg.sender_id);
                const reactions = msg.reactions || {};
                const hasReactions = Object.keys(reactions).length > 0;

                return (
                  <div key={msg.message_id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-1.5 relative group`} data-testid={`group-msg-${msg.message_id}`}>
                    {!isOwn && (
                      <div className="w-9 shrink-0 mr-2">
                        {showAvatar && (
                          <Avatar className="w-9 h-9 border border-border/40">
                            <AvatarImage src={msg.sender_picture} />
                            <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">{(msg.sender_username || msg.sender_name || '?')[0]?.toUpperCase()}</AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                    )}
                    <div className="max-w-[75%]">
                      {showAvatar && !isOwn && (
                        <p className="text-xs text-primary font-semibold ml-1 mb-1">{handleWithFallback(msg.sender_username, msg.sender_name)}</p>
                      )}
                      <div
                        onMouseDown={() => handleLongPressStart(msg.message_id)}
                        onMouseUp={handleLongPressEnd}
                        onMouseLeave={handleLongPressEnd}
                        onTouchStart={() => handleLongPressStart(msg.message_id)}
                        onTouchEnd={handleLongPressEnd}
                        onDoubleClick={() => setReactionPickerMsg(msg.message_id)}
                        onContextMenu={(e) => { e.preventDefault(); setReactionPickerMsg(msg.message_id); }}
                        className={`rounded-2xl px-4 py-2 cursor-pointer select-none transition-colors ${
                          isEmoji ? 'text-4xl bg-transparent px-0 py-0'
                            : isOwn
                              ? (msg.reply_to ? 'bg-primary/55 text-white rounded-br-sm shadow-sm shadow-primary/10' : 'bg-primary text-white rounded-br-sm shadow-sm shadow-primary/20')
                              : (msg.reply_to ? 'bg-card/50 border border-border/30 rounded-bl-sm' : 'bg-card border border-border/50 rounded-bl-sm')
                        }`}
                      >
                        {/* Quoted parent preview (when this message is a reply) */}
                        {msg.reply_to && (
                          <div
                            className={`mb-1.5 px-2 py-1.5 rounded-md border-l-2 ${
                              isOwn
                                ? 'bg-white/15 border-white/70 text-white/85'
                                : 'bg-primary/10 border-primary text-foreground/80'
                            }`}
                          >
                            <p className={`text-[10px] font-semibold ${isOwn ? 'text-white' : 'text-primary'}`}>
                              {msg.reply_to.sender_id === user?.user_id
                                ? 'You'
                                : handleWithFallback(msg.reply_to.sender_username, msg.reply_to.sender_name)}
                            </p>
                            <p className="text-xs truncate opacity-90">
                              {msg.reply_to.message_type === 'emoji' ? msg.reply_to.preview : (msg.reply_to.preview || '')}
                            </p>
                          </div>
                        )}
                        <p className={isEmoji ? '' : 'text-sm leading-relaxed'}>
                          {isEmoji ? msg.content : renderContent(msg.content)}
                        </p>
                      </div>

                      {reactionPickerMsg === msg.message_id && (
                        <div className={`absolute ${isOwn ? 'right-0' : 'left-8'} -top-2 z-10 bg-card border border-border/50 rounded-full px-2 py-1 flex gap-0.5 shadow-xl animate-in fade-in zoom-in-95`} data-testid="reaction-picker">
                          {REACTION_EMOJIS.map(em => (
                            <button key={em} onClick={() => handleReaction(msg.message_id, em)} className="text-lg hover:scale-125 transition-transform p-0.5 hover:bg-primary/10 rounded-full" data-testid={`react-${em}`}>
                              {em}
                            </button>
                          ))}
                          {/* Reply action — same surface as the reaction picker so users discover both via one gesture (long-press / double-click / right-click). */}
                          <button
                            onClick={() => startReply(msg)}
                            className="text-xs px-2 py-0.5 ml-1 rounded-full bg-primary/15 text-primary hover:bg-primary/25 transition-colors flex items-center gap-1"
                            data-testid="reply-action"
                            aria-label="Reply"
                          >
                            ↩ Reply
                          </button>
                          <button onClick={() => setReactionPickerMsg(null)} className="text-muted-foreground hover:text-foreground p-0.5 ml-1">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}

                      {hasReactions && (
                        <div className={`flex flex-wrap gap-1 mt-0.5 ${isOwn ? 'justify-end' : 'justify-start ml-1'}`}>
                          {Object.entries(reactions).map(([emoji, userIds]) => {
                            const isMine = userIds.includes(user?.user_id);
                            const count = userIds.length;
                            return (
                              <button key={emoji} onClick={() => handleReaction(msg.message_id, emoji)}
                                className={`relative w-7 h-7 rounded-full flex items-center justify-center text-[14px] leading-none transition-all active:scale-90 ${
                                  isMine ? 'bg-primary/20 ring-1 ring-primary/60' : 'bg-card/80 border border-border/50 hover:bg-muted/40'
                                }`}
                                data-testid={`reaction-badge-${emoji}`}
                                aria-label={`${emoji} ${count}`}
                              >
                                <span>{emoji}</span>
                                {count > 1 && (
                                  <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center leading-none">
                                    {count > 9 ? '9+' : count}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <p className={`text-[10px] text-muted-foreground/70 mt-1 ${isOwn ? 'text-right mr-1' : 'ml-1'}`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}

        {/* Typing indicator */}
        {typingNames.length > 0 && (
          <div className="flex items-center gap-2 pl-9 py-1" data-testid="typing-indicator">
            <div className="flex gap-0.5">
              <span className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-xs text-muted-foreground/70 italic">
              {typingNames.length === 1 ? `${typingNames[0]} is typing...` : `${typingNames.length} people typing...`}
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom */}
      {internalUnread > 0 && (
        <button onClick={() => { scrollToBottom(true); setInternalUnread(0); clearUnread(); }}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-primary text-white text-xs px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1 animate-bounce z-10"
          data-testid="new-messages-indicator"
        >
          <ChevronDown className="w-3 h-3" />
          {internalUnread} new message{internalUnread > 1 ? 's' : ''}
        </button>
      )}

      {/* @Mentions dropdown */}
      {showMentions && mentionList.length > 0 && (
        <div className="px-3 py-2 bg-card border-t border-border/50 max-h-36 overflow-y-auto" data-testid="mention-dropdown">
          <p className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1"><AtSign className="w-3 h-3" /> Tap to mention</p>
          {mentionList.map(m => (
            <button key={m.user_id} onClick={() => insertMention(m.name)}
              className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg hover:bg-primary/10 transition-colors text-left"
              data-testid={`mention-${m.user_id}`}
            >
              <Avatar className="w-6 h-6">
                <AvatarImage src={m.picture} />
                <AvatarFallback className="bg-primary/20 text-primary text-[8px]">{m.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{m.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Quick Emojis */}
      {showEmojis && (
        <div className="px-3 py-2 bg-card border-t border-border/50 flex gap-1.5 justify-center flex-wrap">
          {QUICK_EMOJIS.map((emoji) => (
            <button key={emoji} onClick={() => handleEmojiSend(emoji)} className="text-xl hover:scale-125 transition-transform p-0.5">
              {emoji}
            </button>
          ))}
        </div>
      )}

      {/* Input — matches ChatPage style */}
      <div className="bg-card border-t border-border/50 px-4 py-3 shrink-0">
        {/* Replying-to banner — sits inside the input bar so it animates as
            one unit with the keyboard. Tap X to drop the reply target. */}
        {replyTo && (
          <div
            className="flex items-stretch gap-2 mb-2 rounded-lg bg-primary/10 border-l-4 border-primary overflow-hidden"
            data-testid="event-chat-reply-banner"
          >
            <div className="flex-1 min-w-0 py-1.5 pl-2 pr-1">
              <p className="text-[11px] font-semibold text-primary">
                Replying to {replyTo.sender_id === user?.user_id ? 'yourself' : handleWithFallback(replyTo.sender_username, replyTo.sender_name)}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {replyTo.message_type === 'emoji' ? replyTo.content : (replyTo.content || '')}
              </p>
            </div>
            <button
              type="button"
              onClick={cancelReply}
              className="px-2 hover:bg-muted/40 transition-colors"
              data-testid="event-chat-reply-cancel"
              aria-label="Cancel reply"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        )}
        <form onSubmit={handleSend} className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => { setShowEmojis(!showEmojis); setShowMentions(false); }} className="rounded-full shrink-0 h-9 w-9 p-0 hover:bg-primary/10" data-testid="group-chat-emoji-btn">
            <Smile className={`w-5 h-5 ${showEmojis ? 'text-primary' : 'text-muted-foreground'}`} />
          </Button>
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              type="text"
              placeholder="Type a message…"
              value={newMessage}
              onChange={handleTyping}
              className="rounded-full pr-10"
              data-testid="group-chat-input"
            />
            <button type="button"
              onClick={() => {
                setShowMentions(!showMentions);
                setShowEmojis(false);
                setMentionFilter('');
                if (!showMentions) {
                  const pos = inputRef.current?.selectionStart || newMessage.length;
                  const before = newMessage.substring(0, pos);
                  const after = newMessage.substring(pos);
                  setNewMessage(before + '@' + after);
                  inputRef.current?.focus();
                }
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
              data-testid="mention-btn"
              title="Mention someone"
            >
              <AtSign className="w-4 h-4" />
            </button>
          </div>
          <Button type="submit" size="sm" disabled={!newMessage.trim() || !connected}
            className="rounded-full bg-primary hover:bg-primary/90 disabled:opacity-50"
            data-testid="group-chat-send-btn"
          >
            <Send className="w-5 h-5" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default GlobalEventChat;
